import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES, getTestingJobOrderEditRoute, getTestingJobOrderRecipientsRoute } from "@/constants/routes";
import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_components/table";
import { Edit, Eye, Copy, Users } from "lucide-react";
import { toast } from "react-hot-toast";
import { API_CONFIG } from "@/constants/api";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { customInstance } from "@/lib/axios-instance";
import { TRANS_MASTER_STATUS } from "@/enums/trans-master-status";
import { getErrorMessage } from "@/lib/error-utils";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

interface JobOrder {
  id: number;
  jobId: string;
  campaignName: string;
  status?: string;
  potentialCandidates: Array<{ id: string; recipientId?: string }>;
}

export default function JobOrderMgmtPage() {
  return (
    <PageAccessGuard authority="ADMIN_TESTING_VIEW">
      <JobOrderMgmtPageContent />
    </PageAccessGuard>
  );
}

function JobOrderMgmtPageContent() {
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const [jobOrders, setJobOrders] = useState<JobOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<number | null>(null);
  const hasFetched = useRef(false);

  // Fetch job orders
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchJobOrders();
    }
  }, []);

  const fetchJobOrders = async () => {
    try {
      setIsLoading(true);

      const response = await customInstance<JobOrder[]>({
        url: API_CONFIG.ENDPOINTS.TEMP_JOB_ORDER,
        method: 'GET'
      });

      setJobOrders(response.data);
    } catch (error) {
      console.error('Error fetching job orders:', getErrorMessage(error));
      hasFetched.current = false; // Reset on error to allow retry
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (id: number, action: 'publish' | 'close') => {
    try {
      setIsUpdating(id);

      await customInstance({
        url: `${API_CONFIG.ENDPOINTS.TEMP_JOB_ORDER}/update/id/${id}/action/${action}`,
        method: 'PUT'
      });

      toast.success(`Job order ${action}ed successfully`);

      // Update local state
      setJobOrders(prev => prev.map(job =>
        job.id === id
          ? { ...job, status: action === 'publish' ? 'Published' : TRANS_MASTER_STATUS.CLOSED }
          : job
      ));
    } catch (error) {
      console.error(`Error ${action}ing job order:`, getErrorMessage(error));
      handleError(error);
    } finally {
      setIsUpdating(null);
    }
  };

  const handleEdit = (id: number) => {
    navigate(getTestingJobOrderEditRoute(id));
  };

  const handleCopy = (jobOrder: JobOrder) => {
    navigate(ROUTES.TESTING_JOB_ORDER_CREATE, {
      state: { copyFrom: jobOrder }
    });
  };

  const handleManageRecipients = (id: number) => {
    navigate(getTestingJobOrderRecipientsRoute(id));
  };

  if (isLoading) {
    return <div className="p-6">{UI_MESSAGES.LOADING}</div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Job Order Management</h1>
            <p className="text-gray-600 mt-2">Manage job orders and their candidates</p>
          </div>
          {/* <Button
            onClick={() => navigate(ROUTES.TESTING_JOB_ORDER_CREATE)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Job Order
          </Button> */}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Job Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="font-semibold text-gray-900">Job No</TableHead>
                  <TableHead className="font-semibold text-gray-900">Campaign</TableHead>
                  <TableHead className="font-semibold text-gray-900">Status</TableHead>
                  <TableHead className="font-semibold text-gray-900">Recipients</TableHead>
                  <TableHead className="font-semibold text-gray-900">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobOrders.map((jobOrder) => (
                  <TableRow key={jobOrder.id}>
                    <TableCell className="font-medium">{jobOrder.jobId}</TableCell>
                    <TableCell>{jobOrder.campaignName}</TableCell>
                    <TableCell>{jobOrder.status}</TableCell>
                    <TableCell>
                      {jobOrder.potentialCandidates.map(candidate => candidate.recipientId || candidate.id).join(', ')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(jobOrder.id)}
                          className="h-8 w-8 p-0"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleManageRecipients(jobOrder.id)}
                          className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700"
                          title="Manage Recipients"
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        {jobOrder.potentialCandidates && jobOrder.potentialCandidates.length > 0 && jobOrder.status !== TRANS_MASTER_STATUS.CLOSED && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAction(jobOrder.id, 'publish')}
                            // disabled={isUpdating === jobOrder.id || jobOrder.status !== TRANS_MASTER_STATUS.CLOSED}
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                            title="Publish"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {jobOrder.status === TRANS_MASTER_STATUS.OUTREACHED && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAction(jobOrder.id, 'close')}
                            disabled={isUpdating === jobOrder.id}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            title="Close"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(jobOrder)}
                          className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                          title="Copy"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {jobOrders.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No job orders found. Click "Add Job Order" to create your first job order.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}